/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	addListener,
	combineReducers,
	configureStore,
	createListenerMiddleware,
	TypedAddListener,
	TypedStartListening,
	TypedStopListening,
} from '@reduxjs/toolkit';
import { createBrowserHistory, History } from 'history';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { routerMiddleware, routerReducer } from 'react-router-redux';
import createOidcMiddleware, { reducer as oidcReducer } from 'redux-oidc';
import { reducers } from './store/index';
import userManager from './userManager';

export const history = createBrowserHistory({ basename: '/' });

const appReducer = combineReducers(Object.assign({}, reducers, { routing: routerReducer, oidc: oidcReducer }));

const listenerMiddleware = createListenerMiddleware();

// Redux DevTools are included by default.
// Default middleware includes redux-thunk. In development, the default also includes
// 1. immutability check middleware
// 2. serializability check middleware
// NOTE: we are turning off the serializable check for oidc since we don't own that state.
const configureAppStore = (appStoreHistory: History, initialState?: any) =>
	configureStore({
		preloadedState: initialState,
		reducer: appReducer,
		middleware: (getDefaultMiddleware) =>
			getDefaultMiddleware({
				serializableCheck: false,
				immutableCheck: {
					warnAfter: 150,
					ignoredPaths: ['distributionList.file', 'surveyImage.file'],
				},
			}).concat(createOidcMiddleware(userManager), routerMiddleware(appStoreHistory), listenerMiddleware.middleware),
	});
export default configureAppStore;

const store = configureAppStore(history);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export type AppStartListening = TypedStartListening<RootState, AppDispatch>;
export type AppStopListening = TypedStopListening<RootState, AppDispatch>;
export type AppAddListener = TypedAddListener<RootState, AppDispatch>;

export const startAppListening = listenerMiddleware.startListening as AppStartListening;
export const stopAppListening = listenerMiddleware.stopListening as AppStopListening;

export const addAppListener = addListener as TypedAddListener<RootState, AppDispatch>;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Enable Webpack hot module replacement for reducers.
export const getStore = () => {
	if (module.hot) {
		module.hot.accept('./store', () => store.replaceReducer(appReducer as any));
	}
	return store;
};
